import { parseJson } from '@gnsx/genesys.js';

const GLB_HEADER_LENGTH = 12;
const GLB_CHUNK_HEADER_LENGTH = 8;
const GLB_MAGIC = 0x46546C67;
const GLB_VERSION = 2;
const GLB_JSON_CHUNK_TYPE = 0x4E4F534A;
const GLB_BIN_CHUNK_TYPE = 0x004E4942;

interface GlbChunk {
  type: number;
  length: number;
  dataOffset: number;
}

function padLengthTo4Bytes(length: number): number {
  return Math.ceil(length / 4) * 4;
}

function readGlbChunks(arrayBuffer: ArrayBuffer): { totalLength: number; chunks: GlbChunk[] } {
  if (arrayBuffer.byteLength < GLB_HEADER_LENGTH) {
    throw new Error('Invalid GLB buffer: header is too small.');
  }
  const view = new DataView(arrayBuffer);
  const magic = view.getUint32(0, true);
  const version = view.getUint32(4, true);
  const totalLength = view.getUint32(8, true);
  if (magic !== GLB_MAGIC) {
    throw new Error('Invalid GLB buffer: bad magic.');
  }
  if (version !== GLB_VERSION) {
    throw new Error(`Invalid GLB buffer: unsupported version ${version}.`);
  }
  if (totalLength > arrayBuffer.byteLength) {
    throw new Error('Invalid GLB buffer: declared length exceeds actual length.');
  }

  const chunks: GlbChunk[] = [];
  let offset = GLB_HEADER_LENGTH;
  while (offset + GLB_CHUNK_HEADER_LENGTH <= totalLength) {
    const length = view.getUint32(offset, true);
    const type = view.getUint32(offset + 4, true);
    const dataOffset = offset + GLB_CHUNK_HEADER_LENGTH;
    if (dataOffset + length > totalLength) {
      throw new Error('Invalid GLB buffer: chunk exceeds declared total length.');
    }
    chunks.push({ type, length, dataOffset });
    offset = dataOffset + length;
  }

  return { totalLength, chunks };
}

export function extractGlbJson(arrayBuffer: ArrayBuffer): unknown | null {
  const { chunks } = readGlbChunks(arrayBuffer);
  const jsonChunk = chunks.find(chunk => chunk.type === GLB_JSON_CHUNK_TYPE);
  if (!jsonChunk) {
    return null;
  }
  const bytes = new Uint8Array(arrayBuffer, jsonChunk.dataOffset, jsonChunk.length);
  const text = new TextDecoder().decode(bytes).replace(/\u0000+$/g, '').trimEnd();
  if (!text) {
    return null;
  }
  return parseJson(text);
}

export function rewriteGlbJsonPaths(
  arrayBuffer: ArrayBuffer,
  rewriteJson: (jsonData: unknown) => unknown
): ArrayBuffer {
  const { chunks } = readGlbChunks(arrayBuffer);
  const jsonChunk = chunks.find(chunk => chunk.type === GLB_JSON_CHUNK_TYPE);
  if (!jsonChunk) {
    throw new Error('GLB JSON chunk not found.');
  }

  const jsonBytes = new Uint8Array(arrayBuffer, jsonChunk.dataOffset, jsonChunk.length);
  const jsonText = new TextDecoder().decode(jsonBytes).replace(/\u0000+$/g, '').trimEnd();
  if (!jsonText) {
    throw new Error('GLB JSON chunk is empty.');
  }
  const parsedJson = parseJson(jsonText);
  const rewrittenJson = rewriteJson(parsedJson);
  const rewrittenText = JSON.stringify(rewrittenJson);
  const rewrittenBytes = new TextEncoder().encode(rewrittenText);
  const rewrittenPaddedLength = padLengthTo4Bytes(rewrittenBytes.length);

  const outputChunks: Array<{ type: number; bytes: Uint8Array }> = [];
  for (const chunk of chunks) {
    if (chunk.type === GLB_JSON_CHUNK_TYPE) {
      const padded = new Uint8Array(rewrittenPaddedLength);
      padded.set(rewrittenBytes);
      padded.fill(0x20, rewrittenBytes.length); // JSON padding uses spaces.
      outputChunks.push({ type: GLB_JSON_CHUNK_TYPE, bytes: padded });
      continue;
    }

    const source = new Uint8Array(arrayBuffer, chunk.dataOffset, chunk.length);
    const paddedLength = padLengthTo4Bytes(chunk.length);
    const padded = new Uint8Array(paddedLength);
    padded.set(source);
    if (chunk.type === GLB_BIN_CHUNK_TYPE && paddedLength > chunk.length) {
      padded.fill(0x00, chunk.length);
    }
    outputChunks.push({ type: chunk.type, bytes: padded });
  }

  const totalLength = GLB_HEADER_LENGTH
    + outputChunks.reduce((sum, chunk) => sum + GLB_CHUNK_HEADER_LENGTH + chunk.bytes.length, 0);
  const output = new ArrayBuffer(totalLength);
  const outputView = new DataView(output);
  const outputBytes = new Uint8Array(output);
  outputView.setUint32(0, GLB_MAGIC, true);
  outputView.setUint32(4, GLB_VERSION, true);
  outputView.setUint32(8, totalLength, true);

  let offset = GLB_HEADER_LENGTH;
  for (const chunk of outputChunks) {
    outputView.setUint32(offset, chunk.bytes.length, true);
    outputView.setUint32(offset + 4, chunk.type, true);
    outputBytes.set(chunk.bytes, offset + GLB_CHUNK_HEADER_LENGTH);
    offset += GLB_CHUNK_HEADER_LENGTH + chunk.bytes.length;
  }

  return output;
}
